import { formatFactorySizeForStorefront } from "../../../constants/factorySizeOptions";

export type ProfileField = {
  label: string;
  value: string;
  /** When set, CompanyOverview renders the link as an icon instead of raw URL text. */
  linkDisplay?: "text" | "icon";
};
export type ProfileSection = { id: string; title: string; fields: ProfileField[] };

const CODE_LABELS: Record<string, Record<string, string>> = {
  mainMarkets: {
    north_america: "North America",
    europe: "Europe",
    asia_pacific: "Asia Pacific",
    middle_east: "Middle East",
    south_america: "South America",
    africa: "Africa",
    domestic: "Domestic",
  },
  paymentTerms: {
    advance: "Advance Payment",
    cod: "Cash on Delivery",
    lc: "Letter of Credit",
  },
  paymentMethods: {
    credit_card: "Credit Card",
    cash: "Cash",
    cheque: "Cheque",
    demand_draft: "Demand Draft",
    paypal: "PayPal",
    moneygram: "MoneyGram",
    western_union: "Western Union",
    others: "Others",
  },
  acceptedDeliveryTerms: {
    fob: "FOB",
    cif: "CIF",
    exw: "EXW",
    ddp: "DDP",
    cfr: "CFR",
  },
  documentType: {
    gst: "GST",
    trade_license: "Trade License",
    incorporation: "Incorporation Certificate",
    other: "Other",
  },
  legalStatus: {
    unregistered: "Unregistered",
    registered: "Registered",
    nonprofit: "Non-profit",
  },
  averageLoadTimeUnit: {
    days: "Days",
    weeks: "Weeks",
    months: "Months",
  },
};

const hasDisplayValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    return lower !== "n/a" && lower !== "na" && lower !== "null";
  }
  if (Array.isArray(value)) return value.some(hasDisplayValue);
  return false;
};

const formatCode = (code: string, mapKey?: string): string => {
  const normalized = code.trim().toLowerCase();
  if (mapKey && CODE_LABELS[mapKey]?.[normalized]) {
    return CODE_LABELS[mapKey][normalized];
  }
  return code
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const parseStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item.trim() : String(item ?? "").trim()))
      .filter(Boolean);
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith("[") && text.endsWith("]")) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) return parseStringList(parsed);
      } catch {
        // fall through
      }
    }
    return text
      .split(/[,|]/)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [];
};

export const formatDisplayList = (value: unknown, mapKey?: string): string => {
  const items = parseStringList(value);
  if (items.length === 0) return "";
  return items.map((item) => formatCode(item, mapKey)).join(", ");
};

export const formatDisplayValue = (value: unknown, mapKey?: string): string => {
  if (!hasDisplayValue(value)) return "";
  if (Array.isArray(value) || (typeof value === "string" && value.includes(","))) {
    return formatDisplayList(value, mapKey);
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (mapKey && CODE_LABELS[mapKey]?.[trimmed.toLowerCase()]) {
      return CODE_LABELS[mapKey][trimmed.toLowerCase()];
    }
    return trimmed;
  }
  return String(value);
};

const pickField = (
  fields: ProfileField[],
  label: string,
  value: unknown,
  mapKey?: string,
  options?: { linkDisplay?: ProfileField["linkDisplay"] },
) => {
  const formatted = formatDisplayValue(value, mapKey);
  if (formatted) {
    fields.push({
      label,
      value: formatted,
      ...(options?.linkDisplay ? { linkDisplay: options.linkDisplay } : {}),
    });
  }
};

const resolveIndustries = (
  company?: Record<string, unknown>,
  businessInfo?: Record<string, unknown>,
  seller?: Record<string, unknown>,
  resolveIndustryLabel?: (value: string) => string,
): string => {
  const companyObj = company || {};
  const businessInfoObj = businessInfo || {};
  const sellerObj = seller || {};
  const sellerCompanyObj = (sellerObj.company as Record<string, unknown>) || {};
  const extended = (companyObj.businessProfileExtended as Record<string, unknown>) || {};
  const sellerExtended =
    (sellerCompanyObj.businessProfileExtended as Record<string, unknown>) || {};

  const pickFromEntry = (item: unknown) => {
    if (typeof item === "string") return item.trim();
    if (item && typeof item === "object") {
      const o = item as Record<string, unknown>;
      const raw =
        o.name || o.label || o.title || o.industryName || o.id || o.categoryId || o.value;
      return typeof raw === "string" ? raw.trim() : "";
    }
    return "";
  };

  const parseSource = (src: unknown): string[] => {
    if (Array.isArray(src)) return src.map(pickFromEntry).filter(Boolean);
    return parseStringList(src).map(pickFromEntry).filter(Boolean);
  };

  const multiValues = [
    ...parseSource(sellerObj.industryTypes),
    ...parseSource(sellerCompanyObj.industryTypes),
    ...parseSource(sellerExtended.industryTypes),
    ...parseSource(sellerExtended.industries),
    ...parseSource(companyObj.industryTypes),
    ...parseSource(extended.industryTypes),
    ...parseSource(extended.industries),
    ...parseSource(businessInfoObj.industryTypes),
    ...parseSource(businessInfoObj.industries),
  ];

  const unique = Array.from(new Set(multiValues));
  if (unique.length > 0) {
    return unique
      .map((value) =>
        resolveIndustryLabel ? resolveIndustryLabel(String(value)) : String(value),
      )
      .join(", ");
  }

  const direct = String(
    sellerObj.industry || sellerCompanyObj.industry || companyObj.industry || "",
  ).trim();
  if (!direct) return "";

  return direct
    .split(/[,|]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((value) =>
      resolveIndustryLabel ? resolveIndustryLabel(String(value)) : String(value),
    )
    .join(", ");
};

export function buildBusinessProfileSections(options: {
  company?: Record<string, unknown>;
  businessInfo?: Record<string, unknown>;
  companyDescription?: string;
  resolveIndustryLabel?: (value: string) => string;
  seller?: Record<string, unknown>;
}): {
  description: string | null;
  highlights: ProfileField[];
  sections: ProfileSection[];
} {
  const { company = {}, businessInfo = {}, companyDescription, resolveIndustryLabel, seller } =
    options;

  const merged = { ...businessInfo, ...company };
  const industries = resolveIndustries(company, businessInfo, seller, resolveIndustryLabel);
  const factorySize = formatFactorySizeForStorefront(
    merged.factorySize || merged.totalFactorySize,
  );
  const location = [merged.city, merged.state, merged.country]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(", ");

  const plainDescription =
    (typeof companyDescription === "string" && companyDescription.trim()) ||
    (typeof merged.description === "string" && merged.description.trim()) ||
    null;

  const highlights: ProfileField[] = [];
  pickField(highlights, "Business Type", merged.businessType);
  pickField(highlights, "Company Size", merged.companySize);
  pickField(highlights, "Employees", merged.employeeCount);
  pickField(highlights, "Established", merged.foundedYear);
  if (factorySize) highlights.push({ label: "Factory Size", value: factorySize });
  if (location) highlights.push({ label: "Location", value: location });
  if (industries) highlights.push({ label: "Industry", value: industries });

  const companyFields: ProfileField[] = [];
  pickField(companyFields, "Legal Business Name", merged.legalBusinessName);
  pickField(companyFields, "Operating Name", merged.businessName || merged.name);
  pickField(companyFields, "Legal Status", merged.legalStatus, "legalStatus");
  pickField(companyFields, "Registration Type", merged.documentType, "documentType");
  pickField(companyFields, "Business Type", merged.businessType);
  pickField(companyFields, "Company Size", merged.companySize);
  pickField(companyFields, "Employees", merged.employeeCount);
  pickField(companyFields, "Year Established", merged.foundedYear);
  if (industries) companyFields.push({ label: "Industries", value: industries });
  pickField(companyFields, "Annual Turnover", merged.annualTurnover);
  pickField(companyFields, "Business License", merged.businessLicense);
  pickField(companyFields, "Registration Number", merged.registrationNumber);
  pickField(companyFields, "Languages Spoken", merged.languagesSpoken);

  const extended = (merged.businessProfileExtended as Record<string, unknown>) || {};
  const infrastructureImageCount = Array.isArray(extended.infrastructureImages)
    ? (extended.infrastructureImages as unknown[]).filter(
        (url): url is string => typeof url === "string" && url.trim().length > 0,
      ).length
    : 0;

  const factoryWarehouseFields: ProfileField[] = [];
  const totalFactorySize = formatFactorySizeForStorefront(
    merged.totalFactorySize || merged.factorySize,
  );
  if (totalFactorySize) {
    factoryWarehouseFields.push({
      label: "Total Factory Size",
      value: totalFactorySize,
    });
  }
  if (infrastructureImageCount > 0) {
    factoryWarehouseFields.push({
      label: "Infrastructure Image",
      value: `${infrastructureImageCount} image${infrastructureImageCount === 1 ? "" : "s"}`,
    });
  }
  pickField(factoryWarehouseFields, "No. of staff", merged.qcStaffCount);

  const tradeFields: ProfileField[] = [];
  pickField(tradeFields, "Main Markets", merged.mainMarkets, "mainMarkets");
  pickField(tradeFields, "Shipment Modes", merged.shipmentModes);
  const exportPct = merged.exportPercentage;
  if (hasDisplayValue(exportPct)) {
    tradeFields.push({
      label: "Export Share",
      value: `${formatDisplayValue(exportPct)}%`,
    });
  }
  pickField(tradeFields, "Nearest Port", merged.nearestPort);
  const loadTimeValue = formatDisplayValue(merged.averageLoadTimeValue);
  const loadTimeUnit = formatDisplayValue(merged.averageLoadTimeUnit, "averageLoadTimeUnit");
  if (loadTimeValue) {
    tradeFields.push({
      label: "Average Lead Time",
      value: loadTimeUnit ? `${loadTimeValue} ${loadTimeUnit}` : loadTimeValue,
    });
  }
  pickField(tradeFields, "Delivery Terms", merged.acceptedDeliveryTerms, "acceptedDeliveryTerms");
  const minCurrency = formatDisplayValue(merged.minOrderValueCurrency);
  const minAmount = formatDisplayValue(merged.minOrderValueAmount);
  if (minAmount) {
    tradeFields.push({
      label: "Minimum Order Value",
      value: minCurrency ? `${minCurrency} ${minAmount}` : minAmount,
    });
  }
  pickField(tradeFields, "Payment Currency", merged.acceptedPaymentCurrency);
  pickField(tradeFields, "Payment Methods", merged.paymentMethods, "paymentMethods");
  pickField(tradeFields, "Payment Terms", merged.paymentTerms, "paymentTerms");

  const qualityFields: ProfileField[] = [];
  const qualityFromCompany = parseStringList(merged.qualityCertifications);
  if (qualityFromCompany.length > 0) {
    qualityFields.push({
      label: "Quality Certifications",
      value: qualityFromCompany.join(", "),
    });
  }
  const qualityFromInfo = parseStringList(businessInfo.qualityCertifications);
  if (qualityFromInfo.length > 0) {
    qualityFields.push({
      label: "Quality Standards",
      value: qualityFromInfo.join(", "),
    });
  }

  const onlineFields: ProfileField[] = [];
  pickField(onlineFields, "Website", merged.website || merged.companyWebsite);
  pickField(onlineFields, "Catalog", merged.facebookUrl, undefined, {
    linkDisplay: "icon",
  });
  pickField(onlineFields, "LinkedIn", merged.linkedinUrl);
  pickField(onlineFields, "Twitter / X", merged.twitterUrl);
  pickField(onlineFields, "YouTube", merged.youtubeUrl);
  pickField(onlineFields, "Instagram", merged.instagramUrl);
  pickField(onlineFields, "Company Video", merged.companyVideoUrl);

  const sections: ProfileSection[] = [
    { id: "company", title: "Company Information", fields: companyFields },
    {
      id: "factory-warehouse",
      title: "Factory & Warehouse Details",
      fields: factoryWarehouseFields,
    },
    { id: "trade", title: "Trade & Shipping", fields: tradeFields },
    { id: "quality", title: "Quality & Compliance", fields: qualityFields },
    { id: "online", title: "Online Presence", fields: onlineFields },
  ].filter((section) => section.fields.length > 0);

  return {
    description: plainDescription,
    highlights,
    sections,
  };
}
